import cn from 'classnames';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { DownloadReportButton } from '@webapp/account/src/components/download-report-button';
import { ResultsContext } from '@webapp/account/src/pages/survey/results/context';
import { getAllLanguages, getSurveyLanguages } from '@webapp/account/src/resources/multi-lang/async';
import { selectMultiLang } from '@webapp/account/src/resources/multi-lang/selectors';
import { selectLicenseInfo } from '@webapp/account/src/resources/profile/selectors';
import { Actions, refreshQuestions } from '@webapp/account/src/resources/reports';
import type { ReportStatus } from '@webapp/account/src/resources/reports/reports';
import { selectMinFilterDate, selectSaveNotComplete } from '@webapp/account/src/resources/survey/selectors';

import { DateRangeFilter } from '@webapp/common/components/date-range-filter';
import { Select } from '@webapp/common/components/select';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { useAction, useActions } from '@webapp/common/hooks/use-actions';
import { useToggle } from '@webapp/common/hooks/use-toggle';
import { DATE_FORMAT, today } from '@webapp/common/lib/date';
import { LocalStorage } from '@webapp/common/lib/storage';

import { ComparePeriodsChips, ComparisonPeriodsModal } from '../compare-periods';
import { DownloadResultsModal } from '../download';
import { ReportStatusOptions } from '../lib';

import css from './header.css';

export const ResultsLayoutHeader: FC<{
    surveyId: number;
    showFilesOptions?: boolean;
    showAudio?: boolean;
    showDownload?: boolean;
    showDateFilter?: boolean;
    showFilters?: boolean;
    hideSingle?: boolean;
    hasTestQuestions?: boolean;
    hasTestsOnFewPages?: boolean;
    isPublic?: boolean;
    compareAllowed?: boolean;
    defaultLang?: string;
    multiLang?: string;
}> = ({
    hideSingle,
    showAudio,
    showDateFilter = true,
    showDownload = true,
    showFilesOptions,
    showFilters = true,
    surveyId,
    hasTestQuestions,
    hasTestsOnFewPages,
    isPublic,
    compareAllowed,
    defaultLang,
    multiLang,
}) => {
    const { enabled: downloadModalVisible, off: hideDownloadModal, on: showDownloadModal } = useToggle(false);
    const { enabled: compareModalVisible, off: hideCompareModal, on: showCompareModal } = useToggle(false);
    const { globalFilters } = useContext(ResultsContext);
    const patchFilters = useAction(Actions.patchFilters);
    const createdDatetime = useSelector(selectMinFilterDate);
    const showNotComplete = useSelector(selectSaveNotComplete);
    const { isProPlus } = useSelector(selectLicenseInfo);
    const { surveyLanguages, isAllLanguagesLoaded, allLanguages } = useSelector(selectMultiLang);

    const getSurveyLanguagesRequest = useActions(getSurveyLanguages);
    const getAllLanguagesRequest = useActions(getAllLanguages);
    const refreshQuestionsRequest = useAction(refreshQuestions);

    // В ЛК режим доступен только на PRO+, в публичных результатах — по настройке ссылки.
    const compareGated = !isPublic && !isProPlus;
    const compareEnabled = isPublic ? Boolean(compareAllowed) : true;
    const comparePeriods = globalFilters.comparePeriods || [];
    const compareActive = comparePeriods.length > 0;
    const compareVisible = compareEnabled && !compareGated;
    const disableComparePeriods = useCallback(() => patchFilters({ comparePeriods: [] }), [patchFilters]);

    useEffect(() => {
        if (!isAllLanguagesLoaded) {
            getAllLanguagesRequest();
        }
    }, [isAllLanguagesLoaded, getAllLanguagesRequest]);

    useEffect(() => {
        getSurveyLanguagesRequest({ surveyId });
    }, [surveyId, getSurveyLanguagesRequest]);

    const reportLanguageOptions = useMemo(() => {
        const langs = surveyLanguages
            .filter((item) => item.status === 'full')
            .map((item) => ({ label: item.nameRu, value: item.code }));

        if (defaultLang && !langs.find((item) => item.value === defaultLang)) {
            const findFullDefaultLangObject = allLanguages.find((item) => item.code === defaultLang);

            langs.push({ value: defaultLang, label: findFullDefaultLangObject?.nameRu || defaultLang });
        }

        return langs;
    }, [surveyLanguages, allLanguages]);

    const [filters, setFilters] = useState({
        from: globalFilters.dateFrom || createdDatetime,
        to: globalFilters.dateTo || today,
    });

    useEffect(() => {
        if (filters.from !== globalFilters.dateFrom || filters.to !== globalFilters.dateTo) {
            setFilters({
                from: globalFilters.dateFrom,
                to: globalFilters.dateTo,
            });
        }
    }, [globalFilters.dateFrom, globalFilters.dateTo]);

    const handleChangeStatus = useCallback(
        (s) => {
            return patchFilters({
                reportStatus: Number(s) as ReportStatus,
            });
        },
        [patchFilters],
    );

    const onChangeDateFilter = useCallback(
        ({ from, to }) => {
            if (isEqual({ from, to }, filters)) return;

            setFilters({ from, to });
            patchFilters({
                dateTo: dayjs(to).format(DATE_FORMAT),
                dateFrom: dayjs(from).format(DATE_FORMAT),
            });
        },
        [filters, patchFilters],
    );

    return (
        <div className={cn(CssUiComponent.NO_PRINT, css.header)}>
            {showNotComplete && showFilters && (
                <div className={cn(css.filter, css.status)}>
                    <Select
                        options={ReportStatusOptions}
                        value={globalFilters.reportStatus}
                        onChange={handleChangeStatus}
                        stretchMenuToLongestOption={true}
                    />
                </div>
            )}
            {multiLang && reportLanguageOptions.length > 0 && (
                <Select
                    value={LocalStorage.get(`survey_${surveyId}_results_lang`) || defaultLang}
                    className={css.langSelect}
                    placeholder='Язык'
                    options={reportLanguageOptions}
                    onChange={(value) => {
                        if (value === defaultLang) {
                            LocalStorage.remove(`survey_${surveyId}_results_lang`);
                        } else {
                            LocalStorage.set(`survey_${surveyId}_results_lang`, value);
                        }
                        refreshQuestionsRequest({ surveyId });
                    }}
                />
            )}
            {showDateFilter && (
                <DateRangeFilter
                    className={css.filters}
                    compareGated={compareGated}
                    comparePeriodsCount={compareVisible && compareActive ? comparePeriods.length + 1 : 0}
                    from={filters.from}
                    minDate={createdDatetime}
                    to={filters.to}
                    onChange={onChangeDateFilter}
                    onCompareDisable={compareEnabled ? disableComparePeriods : undefined}
                    onComparePeriods={compareEnabled ? showCompareModal : undefined}
                />
            )}
            {compareModalVisible && <ComparisonPeriodsModal minDate={createdDatetime} onClose={hideCompareModal} />}
            {showDownload && (
                <>
                    <DownloadReportButton onClick={showDownloadModal} />
                    <DownloadResultsModal
                        hideSingle={hideSingle}
                        surveyId={surveyId}
                        visible={downloadModalVisible}
                        withAudio={showAudio}
                        withFiles={showFilesOptions}
                        hasTestQuestions={hasTestQuestions}
                        hasTestsOnFewPages={hasTestsOnFewPages}
                        onClose={hideDownloadModal}
                        isPublic={isPublic}
                        multiLang={multiLang}
                        defaultLang={defaultLang}
                    />
                </>
            )}
            {compareVisible && compareActive && (
                <div className={css.compareRow}>
                    <ComparePeriodsChips onEdit={showCompareModal} />
                </div>
            )}
        </div>
    );
};
